import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { useDispatch, useI18n, useLogger, useSelector, useStaffCan } from '~Hooks';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Toggle from '~ComponentsImproved/Toggle';
import { useStaffShifts } from '~Providers/StaffShiftsProvider';
import { Staffs } from '~reducers';
import useConfirm from '~Hooks/useConfirm';
import * as actions from '~actions';
import { Box } from '~Styles';
import CustomClockOut from '~Providers/StaffShiftsProvider/Components/CustomClockOut';

const StaffShiftCard = styled.div<{ isSelf: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ isSelf }) =>
    isSelf ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray1100)'};
  border-radius: 8px;
  padding: 16px;
  min-height: 80px;
  border: 1px solid
    ${({ isSelf }) => (isSelf ? 'var(--colorBlueGray800)' : 'var(--colorBlueGray1100)')};
  gap: 16px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/** Catches taps on a disabled Toggle (Toggle uses pointer-events:none) so we can show a toast. */
const ToggleClickCatcher = styled.div`
  display: inline-block;
`;

interface Props {
  staff: Staffs[number];
}

/**
 * Card showing a single staff's clock-in status with a toggle to clock the
 * staff in/out. Shift data and the clockIn/clockOut mutations are sourced
 * from the StaffShiftsProvider via useStaffShifts().
 */
const StaffShiftRow: FC<Props> = ({ staff }) => {
  const { i18n } = useI18n();
  const logger = useLogger('staff-shift-row');
  const dispatch = useDispatch();
  const staffShifts = useStaffShifts();
  const staffCan = useStaffCan();
  const { confirm } = useConfirm();

  const staffId = useSelector((state) => state.staff?.id);
  const country = useSelector((state) => state.country);

  const shift = staffShifts.lookup[staff.id];

  /** Swedish venues are legally required to attach a personnummer to every staff-shift entry. */
  const missingRequiredNationalId = country === 'SE' && !staff.ssn;

  const isClockedIn = Boolean(shift?.clockedInAt && !shift?.clockedOutAt);

  /** Format the shift time as HH:mm or "Yesterday" */
  const formatShiftTime = (iso: string) => {
    const dt = DateTime.fromISO(iso);
    const time = dt.toFormat('HH:mm');

    /** Whole-day difference between the shift's day and today (0 = today, 1 = yesterday). */
    const daysAgo = DateTime.local()
      .startOf('day')
      .diff(dt.startOf('day'), 'days').days;

    if (daysAgo <= 0) {
      return time;
    }
    if (daysAgo === 1) {
      return `${i18n('StaffShifts.Yesterday')} ${time}`;
    }
    /** Older than yesterday — show short date + time, e.g. "25/5 14:30". */
    return `${dt.toFormat('d/L')} ${time}`;
  };

  /** Label + time shown next to the toggle, based on the latest shift state. */
  let timeLabel: string;
  let timeValue: string;
  if (shift?.clockedInAt && !shift?.clockedOutAt) {
    timeLabel = i18n('StaffShifts.ClockedInAt');
    timeValue = formatShiftTime(shift.clockedInAt);
  } else if (shift?.clockedOutAt) {
    timeLabel = i18n('StaffShifts.ClockedOutAt');
    timeValue = formatShiftTime(shift.clockedOutAt);
  } else {
    timeLabel = i18n('StaffShifts.NotClockedIn');
    timeValue = '—';
  }

  /** Worked time as HH:MM h. Hidden when 0. */
  const workedMinutes = Math.floor(staffShifts.workedMinutesTotal[staff.id] ?? 0);
  const showWorkedHours = workedMinutes > 0;
  const workedHoursText = `${Math.floor(workedMinutes / 60)}:${(workedMinutes % 60)
    .toString()
    .padStart(2, '0')} h`;

  /** True while the clockIn/clockOut mutation is in flight */
  const [mutating, setMutating] = useState(false);
  /** True while we're refetching the shifts query after a successful mutation */
  const [refetching, setRefetching] = useState(false);
  /** True while the back-dated clock-out picker is open for another staff. */
  const [showClockOutPicker, setShowClockOutPicker] = useState(false);
  /** Bumped to force-remount the Toggle (e.g. snap back when the picker is cancelled). */
  const [remountTick, setRemountTick] = useState(0);

  /** Disabled while a mutation or follow-up refetch is in flight. */
  const isLoading = mutating || refetching;

  /** Others can only be toggled with ASSUME_ROLE permission. */
  const isSelf = staff.id === staffId;
  const canActOnOthers = staffCan.ASSUME_ROLE;
  const blockClockOut = isClockedIn && !isSelf && !canActOnOthers;
  const blockClockIn = !isClockedIn && !isSelf && !canActOnOthers;

  const onToggle = async (nextActive: boolean) => {
    if (isLoading) return;

    /** Acting on another staff: block without permission, otherwise confirm/pick. */
    if (!isSelf) {
      if (!canActOnOthers) {
        logger.warn('Blocked clock in/out of another staff member', {
          nextActive,
          targetStaffId: staff.id,
          loggedInStaffId: staffId,
        });
        dispatch(
          actions.addToast(i18n('StaffShifts.MissingPermissionForOthers'), true, null, true),
        );
        return;
      }

      /** Clocking OUT another: pick a time via the date-picker modal. */
      if (!nextActive) {
        if (!shift) {
          logger.warn('Cannot show clock-out picker without an open shift', {
            staffId: staff.id,
          });
          setRemountTick((t) => t + 1);
          return;
        }
        logger.info('Showing back-dated clock-out picker for another staff', {
          targetStaffId: staff.id,
        });
        setShowClockOutPicker(true);
        /** Snap toggle back to "on" — it only flips after the picker confirms and data updates. */
        setRemountTick((t) => t + 1);
        return;
      }

      /** Clocking IN another: confirm dialog as before. */
      const confirmed = await confirm(i18n('StaffShifts.ConfirmClockIn', staff.name));
      if (!confirmed) {
        logger.info('User cancelled clock-in of another staff member', {
          targetStaffId: staff.id,
        });
        setRemountTick((t) => t + 1);
        return;
      }
    }

    setMutating(true);

    let mutationFailed = false;
    try {
      if (nextActive) {
        await staffShifts.clockIn(staff.id);
      } else {
        await staffShifts.clockOut(staff.id);
      }
    } catch (err) {
      logger.error('Clock in/out mutation failed', {
        staffId: staff.id,
        nextActive,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
      mutationFailed = true;
    }

    setMutating(false);

    if (!mutationFailed) {
      setRefetching(true);
      try {
        await staffShifts.refetch();
      } catch (err) {
        logger.error('Refetch after clock in/out failed', {
          staffId: staff.id,
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setRefetching(false);
      }
    }

    /** Sync the Toggle with the data: confirms on success, snaps back on failure. */
    setRemountTick((t) => t + 1);
  };

  /** Force-remount the Toggle when the shift's clock state changes or we need to snap it back. */
  const toggleKey = `${shift?.id ?? 'none'}-${shift?.clockedInAt ?? ''}-${shift?.clockedOutAt ??
    ''}-${remountTick}`;

  return (
    <StaffShiftCard isSelf={isSelf}>
      <Box display="flex" alignItems="center" gap="xs">
        <Heading level={4} color="var(--colorWhite)" bottom={0}>
          {staff.name}
        </Heading>
        {isSelf && (
          <Paragraph level={5} color="var(--colorBlueGray200)">
            {i18n('StaffShifts.You')}
          </Paragraph>
        )}
      </Box>

      <Footer>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
            {timeLabel}
          </Heading>
          <Paragraph fontWeight={600} level={4} color="var(--colorWhite)" bottom={0}>
            {timeValue}
          </Paragraph>
        </Box>

        {showWorkedHours && (
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
              {i18n('StaffShifts.WorkedToday')}
            </Heading>
            <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
              {workedHoursText}
            </Paragraph>
          </Box>
        )}

        <ToggleClickCatcher
          onClick={() => {
            /** Only react when blocked by permission, not when disabled mid-mutation. */
            if (blockClockOut || blockClockIn) {
              logger.info('Disabled toggle tapped, showing permission toast', {
                staffId: staff.id,
                blockClockOut,
                blockClockIn,
              });
              dispatch(
                actions.addToast(i18n('StaffShifts.MissingPermissionForOthers'), true, null, true),
              );
            }
          }}>
          <Toggle
            key={toggleKey}
            initialValue={isClockedIn}
            onToggle={onToggle}
            disabled={isLoading || blockClockOut || blockClockIn || missingRequiredNationalId}
          />
        </ToggleClickCatcher>
      </Footer>

      {missingRequiredNationalId && (
        <Paragraph level={5} color="var(--colorBlueGray200)">
          {i18n('StaffShifts.NationalIdRequired')}
        </Paragraph>
      )}

      {showClockOutPicker && shift && (
        <CustomClockOut
          shift={shift}
          title={i18n('StaffShifts.ConfirmClockOut', staff.name)}
          subtitle=""
          onComplete={() => {
            setShowClockOutPicker(false);
          }}
          onCancel={() => {
            setShowClockOutPicker(false);
            setRemountTick((t) => t + 1);
          }}
        />
      )}
    </StaffShiftCard>
  );
};

export default StaffShiftRow;
